/**
 * IntentEngine — client-side behaviour tracker + 4-feature response layer.
 *
 * Runs ONLY on marketing / anonymous pages (never inside authed portals)
 * and is entirely rule-based — zero LLM cost. It exposes three UI overlays:
 *
 *   1. Smart Social Proof toaster (bottom-left)
 *      Cycles through recent real signups every 12s so first-time visitors
 *      see that Central Rentals is being adopted across every province.
 *
 *   2. Hesitant-scroll nudge banner
 *      Fires once per session when the visitor scrolls up-down the pricing
 *      page 3+ times in 25s — a classic "considering but not clicking" signal.
 *      Shows a one-tap "Show me the smaller plan" link.
 *
 *   3. Dynamic exit-intent modal
 *      Fires ONCE per session when the mouse leaves the viewport through the
 *      top edge (classic desktop exit signal) OR when the tab visibility flips
 *      to hidden on mobile after 15s+ engagement. Shows a limited-time 15%
 *      discount code that unlocks a Stripe promo in Central Rentals's own
 *      pricing catalog. (Discount codes are static / pre-issued in Stripe —
 *      no cash flows through us.)
 *
 * We deliberately DO NOT record raw pointer events to Mongo — everything is
 * kept in memory on the visitor's device so this respects the "no invasive
 * tracking" PRD constraint.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const SESSION_KEY = "crc_intent_engine";
const NUDGE_SHOWN_KEY = "crc_intent_nudge_shown";
const EXIT_SHOWN_KEY  = "crc_intent_exit_shown";

// Routes where the engine should NOT run — authed portals + checkout so we
// never distract a user mid-payment.
const SILENT_ROUTES = [
  "/dashboard", "/tickets", "/leases", "/messages", "/payments", "/tenants",
  "/properties", "/settings", "/billing", "/admin", "/onboarding", "/reports",
  "/auth", "/login", "/signup", "/logout",
];

const ROLE_LABEL = {
  landlord: "landlord",
  tenant: "tenant",
  admin: "team member",
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    setReduced(!!m?.matches);
    const h = (e) => setReduced(e.matches);
    m?.addEventListener?.("change", h);
    return () => m?.removeEventListener?.("change", h);
  }, []);
  return reduced;
}

// ── Social-proof toaster removed per product direction ────────────────
// The "A landlord in Canada just started using Central Rentals" pop-up
// felt manufactured and was removed in Feb 2026. We keep an empty stub
// so the render tree still resolves cleanly; if you want to bring it
// back, restore the previous git version of this function.
function SocialProofToaster() {
  return null;
}

// ── Hesitant-scroll nudge (pricing-page only) ────────────────────────────
function HesitationNudge({ onShown }) {
  const [visible, setVisible] = useState(false);
  const [province, setProvince] = useState(null);
  const reversalCount = useRef(0);
  const lastDirection = useRef(0);
  const lastY = useRef(0);
  const startedAt = useRef(Date.now());

  // Fetch the visitor's province once on mount — Cloudflare / edge CDN
  // Ingress supplies it via headers, so no third-party geo-IP call. We
  // use it to personalise the banner headline ("Ontario landlord special").
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/growth/province-hint`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.province_name) setProvince(d.province_name); })
      .catch(() => { /* fall back to generic copy */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(NUDGE_SHOWN_KEY) === "1") return;

    const onScroll = () => {
      const y = window.scrollY;
      const dir = y > lastY.current ? 1 : (y < lastY.current ? -1 : 0);
      if (dir && dir !== lastDirection.current) {
        reversalCount.current += 1;
        lastDirection.current = dir;
      }
      lastY.current = y;

      // Threshold: 3 direction reversals within the first 25s on-page
      // == user is scanning back and forth without committing.
      if (
        reversalCount.current >= 3 &&
        Date.now() - startedAt.current > 12_000 &&
        Date.now() - startedAt.current < 60_000
      ) {
        setVisible(true);
        sessionStorage.setItem(NUDGE_SHOWN_KEY, "1");
        onShown?.();
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onShown]);

  if (!visible) return null;
  return (
    <div
      role="dialog"
      aria-labelledby="crc-nudge-title"
      data-testid="hesitation-nudge"
      className="fixed bottom-6 right-6 z-40 max-w-sm bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="p-5">
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="absolute top-3 right-3 text-white/70 hover:text-white text-sm"
          data-testid="hesitation-close"
          aria-label="Close nudge"
        >×</button>
        <div className="text-xs uppercase tracking-widest font-bold text-emerald-100">
          {province ? `${province} landlord special` : "Still comparing?"}
        </div>
        <h3 id="crc-nudge-title" className="mt-1 font-display text-xl font-bold">
          {province ? `Try the $5 starter plan in ${province}` : "Try the $5 starter plan"}
        </h3>
        <p className="mt-1 text-sm text-white/85">
          {province
            ? `Built for ${province} landlords — same rent tracking, same tenant screening, same CRA-ready tax reports. Upgrade anytime.`
            : "Same rent tracking, same tenant screening, same CRA-ready tax reports. Perfect first taste — upgrade anytime."}
        </p>
        <Link
          to="/pricing?plan=starter"
          data-testid="hesitation-cta"
          onClick={() => setVisible(false)}
          className="mt-4 inline-flex items-center gap-1.5 bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-4 py-2.5 rounded-full text-sm shadow-sm"
        >Show me the starter →</Link>
      </div>
    </div>
  );
}

// ── Exit-intent + behavior-based-pricing modal ───────────────────────────
function ExitBehaviorOffer() {
  const [visible, setVisible] = useState(false);
  const startedAt = useRef(Date.now());
  const armed = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(EXIT_SHOWN_KEY) === "1") return;

    // Arm the trigger only after 8s so we don't nag drive-by visitors.
    const armTimer = setTimeout(() => { armed.current = true; }, 8_000);

    const onLeave = (e) => {
      if (!armed.current) return;
      // Desktop: mouse leaves through the top edge.
      if (e.clientY != null && e.clientY <= 4) {
        show();
      }
    };
    const onVisibility = () => {
      if (!armed.current) return;
      if (document.visibilityState === "hidden" && Date.now() - startedAt.current > 15_000) {
        // Mobile / tab-switch fallback.
        show();
      }
    };
    const show = () => {
      setVisible(true);
      sessionStorage.setItem(EXIT_SHOWN_KEY, "1");
      document.removeEventListener("mouseout", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    document.addEventListener("mouseout", onLeave);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  if (!visible) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="crc-exit-title"
      data-testid="exit-behavior-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-950/60 backdrop-blur-sm"
      onClick={() => setVisible(false)}
    >
      <div
        className="relative max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center"
          data-testid="exit-behavior-close"
          aria-label="Close offer"
        >×</button>
        <div className="p-8">
          <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">Wait — one-time offer</div>
          <h3 id="crc-exit-title" className="mt-1 font-display text-2xl font-bold text-slate-900 leading-tight">
            15% off your first 3 months
          </h3>
          <p className="mt-2 text-slate-600 text-sm">
            Apply promo code <span className="inline-block font-mono text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded">STAY15</span> at checkout —
            good for any Central Rentals plan, all 13 provinces &amp; territories.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Link
              to="/pricing?promo=STAY15"
              data-testid="exit-behavior-cta"
              onClick={() => setVisible(false)}
              className="w-full inline-flex justify-center items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-3 rounded-full text-sm shadow-sm transition-colors"
            >
              Claim 15 % off → see plans
            </Link>
            <button
              type="button"
              onClick={() => setVisible(false)}
              className="text-xs text-slate-500 hover:text-slate-700 mt-1"
              data-testid="exit-behavior-decline"
            >No thanks — I&apos;ll browse a bit more</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Root component ──────────────────────────────────────────────────────
export default function IntentEngine() {
  const location = useLocation();
  const reduced = useReducedMotion();
  const { user } = useAuth();
  const [nudgeShown, setNudgeShown] = useState(false);

  // Skip the engine entirely on authed / high-friction paths.
  const path = location.pathname || "/";
  const isSilent = SILENT_ROUTES.some((p) => path.startsWith(p));
  const isPricing = path.startsWith("/pricing");

  const handleNudgeShown = useCallback(() => setNudgeShown(true), []);

  // Never run inside an authenticated session — the social-proof
  // toaster is only meant to convince first-time public visitors.
  // Landlords and tenants already inside the portal should never see it.
  if (user) return null;
  if (isSilent) return null;
  if (reduced) return null; // respect user's motion preference

  return (
    <>
      <SocialProofToaster />
      {isPricing && !nudgeShown && <HesitationNudge onShown={handleNudgeShown} />}
      <ExitBehaviorOffer />
    </>
  );
}
